import { faImage, faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Image from 'next/image'
import { useIntl } from 'react-intl'
import { css, styled } from 'styled-components'

import {
  UnstyledButton,
  VideoOverlay as BaseVideoOverlay,
  PlayButton as BasePlayButton,
} from '@b-stock/fe-core/bstock-next/components'
import { FormattedMessage } from '@b-stock/fe-core/bstock-react'
import { Colors, pxToRem, Typography } from '@b-stock/fe-core/design-system'

import { getLegacyBpStaticPath } from '@/buyer-portal/buyUrlPrefix'

import type { KeyedMedia } from './types'

const placeholder = getLegacyBpStaticPath('images/placeholder-image.png')

const CELL_HEIGHT = 160
const CELL_MAX_WIDTH = 200
const CELL_MIN_WIDTH = 140

export const VISIBLE_THUMBNAILS = 4

const Body = styled.div`
  display: flex;
  overflow-x: auto;
  scrollbar-width: none;
  width: 100%;

  &::-webkit-scrollbar {
    display: none;
  }
`

const cellStyles = css`
  cursor: pointer;
  flex: 1;
  height: ${CELL_HEIGHT}px;
  max-width: ${CELL_MAX_WIDTH}px;
  min-width: ${CELL_MIN_WIDTH}px;
`

const PhotoOverlay = styled.div`
  align-items: center;
  background: rgba(0, 91, 159, 0.18);
  display: flex;
  inset: 0;
  justify-content: center;
  opacity: 0;
  position: absolute;
  transition: opacity 0.15s;
`

const OverlayIcon = styled(FontAwesomeIcon)`
  color: ${Colors.Semantic.Neutral.white};
  font-size: 1.5rem;
`

const PlayButton = styled(BasePlayButton)`
  width: 2.5rem;
  height: 2.5rem;
`

const VideoOverlay = styled(BaseVideoOverlay)`
  max-height: none;
`

const Thumbnail = styled(UnstyledButton)`
  ${cellStyles}
  border-right: 0.0625rem solid ${Colors.Semantic.Neutral.light_grey};
  overflow: hidden;
  position: relative;

  &:last-child {
    border-right: none;
  }

  &:hover ${PhotoOverlay} {
    opacity: 1;
  }
`

const MorePhotosCell = styled(UnstyledButton)`
  ${cellStyles}
  align-items: center;
  background: ${Colors.Semantic.Neutral.light_grey};
  border-left: 0.0625rem solid ${Colors.Semantic.Neutral.light_grey};
  border-radius: 0 0.5rem 0 0;
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  justify-content: center;
  padding: 1.5rem;
  position: sticky;
  right: 0;
  text-align: center;
  z-index: 1;

  ${Typography.Body3}
  color: ${Colors.Semantic.Neutral.dark_grey};
  font-size: ${pxToRem(13)};
`

export const ThumbnailStrip = ({
  items,
  onThumbnailClick,
}: {
  items: KeyedMedia[]
  onThumbnailClick: (
    index: number,
    openTrigger: 'more_tile' | 'thumbnail'
  ) => void
}) => {
  const total = items.length
  const { formatMessage } = useIntl()
  const hiddenCount = Math.max(0, items.length - VISIBLE_THUMBNAILS)

  return (
    <Body>
      {items.slice(0, VISIBLE_THUMBNAILS).map((item, index) => {
        const isVideo = item.type === 'video'
        return (
          <Thumbnail
            key={item.key}
            aria-label={formatMessage(
              { id: 'Auction.details.enhancedGallery.thumbnailAriaLabel' },
              { index: index + 1, total }
            )}
            onClick={() => onThumbnailClick(index, 'thumbnail')}
          >
            <Image
              alt={
                item.title ??
                formatMessage(
                  { id: 'Auction.details.enhancedGallery.thumbnailAlt' },
                  { index: index + 1 }
                )
              }
              fill
              loading="lazy"
              sizes={`${CELL_MAX_WIDTH}px`}
              src={item.urls?.medium ?? item.urls?.thumb ?? placeholder}
              style={{ objectFit: 'cover' }}
            />
            {isVideo ? (
              <VideoOverlay>
                <PlayButton />
              </VideoOverlay>
            ) : (
              <PhotoOverlay aria-hidden>
                <OverlayIcon icon={faMagnifyingGlass} />
              </PhotoOverlay>
            )}
          </Thumbnail>
        )
      })}
      {hiddenCount > 0 && (
        <MorePhotosCell
          aria-label={formatMessage(
            { id: 'Auction.details.enhancedGallery.viewAllAriaLabel' },
            { total }
          )}
          onClick={() => onThumbnailClick(VISIBLE_THUMBNAILS, 'more_tile')}
        >
          <FontAwesomeIcon icon={faImage} />
          <FormattedMessage
            id="Auction.details.enhancedGallery.morePhotos"
            values={{ count: hiddenCount }}
          />
        </MorePhotosCell>
      )}
    </Body>
  )
}
